import clsx from "clsx";
import React, {
  ImgHTMLAttributes,
  ReactNode,
  SyntheticEvent,
  useEffect,
  useState,
  memo,
} from "react";
import { MdImage } from "react-icons/md";
import { ICON_SIZES } from "src/constants/icons";

type TImageStatus = "idle" | "loading" | "loaded" | "error";

export interface ISmartImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src"
> {
  src?: string | null;
  fallbackSrc?: string;
  wrapperClassName?: string;
  imageClassName?: string;
  skeletonClassName?: string;
  errorClassName?: string;
  showSkeleton?: boolean;
  keepImageSpace?: boolean;
  errorContent?: ReactNode;
  skeletonContent?: ReactNode;
  onLoadStatusChange?: (status: TImageStatus) => void;
}

const SmartImageComponent: React.FC<ISmartImageProps> = ({
  src,
  fallbackSrc,
  alt = "",
  className,
  wrapperClassName,
  imageClassName,
  skeletonClassName = clsx(["flex", "items-center", "justify-center"]),
  errorClassName,
  showSkeleton = true,
  keepImageSpace = true,
  errorContent,
  skeletonContent = <MdImage size={ICON_SIZES.SM} />,
  onLoadStatusChange,
  onLoad,
  onError,
  ...imgProps
}) => {
  const [status, setStatus] = useState<TImageStatus>("idle");
  const [currentSrc, setCurrentSrc] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const normalizedSrc = src?.trim();

    if (!normalizedSrc) {
      setCurrentSrc("");
      setStatus("idle");
      return () => {
        cancelled = true;
      };
    }

    const preload = (imageSrc: string, isFallback = false) => {
      const img = new Image();
      img.src = imageSrc;

      setCurrentSrc(imageSrc);

      // 🔥 evita flicker si ya está en cache
      if (img.complete && img.naturalWidth > 0) {
        if (!cancelled) {
          setStatus("loaded");
        }
        return;
      }

      if (!cancelled) {
        setStatus("loading");
      }

      img.onload = () => {
        if (cancelled) return;
        setStatus("loaded");
      };

      img.onerror = () => {
        if (cancelled) return;

        if (!isFallback && fallbackSrc?.trim()) {
          const normalizedFallback = fallbackSrc.trim();

          if (normalizedFallback && normalizedFallback !== imageSrc) {
            preload(normalizedFallback, true);
            return;
          }
        }

        setStatus("error");
      };
    };

    preload(normalizedSrc);

    return () => {
      cancelled = true;
    };
  }, [src, fallbackSrc]);

  useEffect(() => {
    onLoadStatusChange?.(status);
  }, [status, onLoadStatusChange]);

  const handleLoad = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    setStatus("loaded");
    onLoad?.(e);
  };

  const handleError = (e: SyntheticEvent<HTMLImageElement, Event>) => {
    onError?.(e);
  };

  const showImage = !!currentSrc && status !== "error";
  const showLoader = showSkeleton && status === "loading";
  const showError = status === "error";

  return (
    <div
      className={clsx([
        "relative",
        "overflow-hidden",
        "w-full",
        // keepImageSpace && "min-h-[40px]", //
        wrapperClassName,
      ])}
    >
      {showLoader && (
        <div
          className={clsx([
            "absolute",
            "inset-0",
            "animate-pulse",
            "rounded",
            "text-gray-400",
            skeletonClassName,
          ])}
          aria-hidden="true"
        >
          {skeletonContent}
        </div>
      )}

      {showError && (
        <div
          className={clsx([
            "flex",
            "h-full",
            "w-full",
            "items-center",
            "justify-center",
            "rounded",
            "text-sm",
            "text-gray-500",
            "dark:text-gray-400",
            errorClassName,
          ])}
        >
          {errorContent ?? "No se pudo cargar la imagen"}
        </div>
      )}

      {showImage && (
        <img
          {...imgProps}
          alt={alt}
          src={currentSrc}
          onLoad={handleLoad}
          onError={handleError}
          className={clsx([
            "object-cover",
            className,
            imageClassName,
            "transition-opacity",
            "duration-300",
            status === "loaded" ? "opacity-100" : "opacity-0",
          ])}
        />
      )}
    </div>
  );
};

const SmartImage = memo(SmartImageComponent);

export default SmartImage;
